import Decimal from 'decimal.js';

/**
 * P11 B5 PR-B – Chuyến giao nhiều đơn / nhiều điểm (docs/phases/P11.md §10, DEC-260).
 * Hàm thuần dùng chung API + giao diện: trạng thái chuyến / điểm dừng, kiểm điều kiện cứng (kg / m³ / dài),
 * kiểm thứ tự "lấy trước giao" khi đổi thứ tự điểm dừng. Thiếu số (khối, dài) thì báo "chưa đủ dữ liệu", không đoán.
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

export const TRIP_CODE_SUFFIX = 'CX';

export const DISPATCH_TRIP_STATUSES = ['planning', 'locked', 'in_progress', 'completed', 'cancelled'] as const;
export type DispatchTripStatus = (typeof DISPATCH_TRIP_STATUSES)[number];
export const DISPATCH_TRIP_STATUS_LABELS: Record<DispatchTripStatus, string> = {
  planning: 'Đang xếp',
  locked: 'Đã khóa',
  in_progress: 'Đang chạy',
  completed: 'Hoàn tất',
  cancelled: 'Đã hủy',
};

const DISPATCH_TRIP_TRANSITIONS: Record<DispatchTripStatus, DispatchTripStatus[]> = {
  planning: ['locked', 'cancelled'],
  locked: ['planning', 'in_progress', 'cancelled'],
  in_progress: ['completed'],
  completed: [],
  cancelled: [],
};
export const canTransitionTrip = (from: DispatchTripStatus, to: DispatchTripStatus) => DISPATCH_TRIP_TRANSITIONS[from].includes(to);

export const DISPATCH_TRIP_MODES = ['company_vehicle', 'hired_vehicle', 'carrier'] as const;
export type DispatchTripMode = (typeof DISPATCH_TRIP_MODES)[number];
export const DISPATCH_TRIP_MODE_LABELS: Record<DispatchTripMode, string> = {
  company_vehicle: 'Xe công ty',
  hired_vehicle: 'Xe thuê ngoài',
  carrier: 'Nhà vận chuyển (Lalamove…)',
};
/** Phương thức ghi lên phiếu giao khi phiếu vào chuyến (phiếu không có "nhà vận chuyển" → coi như xe thuê). */
export const noteMethodOfTrip = (m: DispatchTripMode): 'company_vehicle' | 'hired_vehicle' => (m === 'company_vehicle' ? 'company_vehicle' : 'hired_vehicle');

export const TRIP_STOP_KINDS = ['pickup', 'drop'] as const;
export type TripStopKind = (typeof TRIP_STOP_KINDS)[number];
export const TRIP_STOP_KIND_LABELS: Record<TripStopKind, string> = { pickup: 'Lấy hàng', drop: 'Giao hàng' };

export const TRIP_STOP_STATUSES = ['pending', 'arrived', 'done', 'skipped', 'failed'] as const;
export type TripStopStatus = (typeof TRIP_STOP_STATUSES)[number];
export const TRIP_STOP_STATUS_LABELS: Record<TripStopStatus, string> = {
  pending: 'Chưa tới',
  arrived: 'Đã tới',
  done: 'Xong',
  skipped: 'Bỏ qua',
  failed: 'Không giao được',
};

export const NOTE_PRIORITIES = [0, 1, 2] as const;
export const NOTE_PRIORITY_LABELS: Record<number, string> = { 0: 'Thường', 1: 'Ưu tiên', 2: 'Gấp' };

/** Sức chở chụp lúc lập chuyến (xe công ty lấy từ danh mục; xe thuê / nhà vận chuyển gõ tay). null = chưa khai. */
export interface TripCapacity {
  loadKg: number | null;
  volumeM3: number | null;
  lengthM: number | null;
}

/** Tải của 1 phiếu (hoặc tổng chuyến). volumeM3 = null khi có dòng chưa có khối tích; lengthM = hàng dài nhất. */
export interface NoteLoad {
  kg: number;
  volumeM3: number | null;
  /** Số dòng chưa có khối tích (không cộng được) */
  unknownVolumeLines: number;
  lengthM: number | null;
}

export interface TripLoad {
  kg: number;
  /** Khối đã biết (m³) – phiếu thiếu khối không cộng vào */
  volumeM3: number;
  /** Số phiếu trong chuyến còn thiếu khối tích */
  unknownVolumeNotes: number;
  maxLengthM: number | null;
}

export const emptyTripLoad = (): TripLoad => ({ kg: 0, volumeM3: 0, unknownVolumeNotes: 0, maxLengthM: null });

/** Cộng phiếu vào tải chuyến. */
export function addLoad(t: TripLoad, n: NoteLoad): TripLoad {
  return {
    kg: new D(t.kg).plus(n.kg).toDecimalPlaces(3).toNumber(),
    volumeM3: new D(t.volumeM3).plus(n.volumeM3 ?? 0).toDecimalPlaces(3).toNumber(),
    unknownVolumeNotes: t.unknownVolumeNotes + (n.volumeM3 === null || n.unknownVolumeLines > 0 ? 1 : 0),
    maxLengthM: n.lengthM === null ? t.maxLengthM : Math.max(t.maxLengthM ?? 0, n.lengthM),
  };
}

export interface CapacityCheck {
  ok: boolean;
  /** Lý do chặn (vượt tải / khối / dài) – tiếng Việt, hiện thẳng cho người xếp */
  errors: string[];
  /** Không chặn: thiếu số để kiểm (chưa khai thùng xe, sản phẩm chưa có khối tích) */
  warnings: string[];
}

const fmt = (n: number, dp = 1) => new D(n).toDecimalPlaces(dp).toNumber().toLocaleString('vi-VN');

/**
 * Điều kiện cứng khi xếp phiếu vào chuyến: kg ≤ tải, m³ ≤ khối xếp được, hàng dài nhất ≤ dài thùng + thò.
 * Thiếu sức chở hoặc thiếu khối / dài của hàng → cảnh báo "chưa đủ dữ liệu", không chặn và không đoán.
 */
export function checkTripCapacity(cap: TripCapacity, load: TripLoad): CapacityCheck {
  const errors: string[] = [];
  const warnings: string[] = [];
  if (cap.loadKg === null) warnings.push('Chưa khai tải trọng xe: chưa kiểm được kg');
  else if (load.kg > cap.loadKg) errors.push(`Vượt tải: ${fmt(load.kg, 0)} kg > ${fmt(cap.loadKg, 0)} kg`);
  if (cap.volumeM3 === null) warnings.push('Chưa khai kích thước thùng xe: chưa kiểm được khối');
  else if (load.volumeM3 > cap.volumeM3) errors.push(`Vượt khối: ${fmt(load.volumeM3, 2)} m³ > ${fmt(cap.volumeM3, 2)} m³`);
  if (load.unknownVolumeNotes > 0) warnings.push(`${load.unknownVolumeNotes} phiếu có hàng chưa có khối tích (chưa đủ dữ liệu)`);
  if (load.maxLengthM !== null) {
    if (cap.lengthM === null) warnings.push('Chưa khai dài thùng xe: chưa kiểm được hàng dài');
    else if (load.maxLengthM > cap.lengthM) errors.push(`Hàng dài ${fmt(load.maxLengthM, 2)} m > xe chở được ${fmt(cap.lengthM, 2)} m`);
  }
  return { ok: errors.length === 0, errors, warnings };
}

export interface StopOrderItem {
  id: number;
  kind: TripStopKind;
  /** Phiếu giao gắn với điểm dừng */
  noteIds: number[];
}

/**
 * Kiểm thứ tự điểm dừng: mỗi phiếu phải lấy hết hàng (mọi điểm lấy của phiếu) trước điểm giao của phiếu.
 * Trả lỗi tiếng Việt theo mã phiếu; rỗng = hợp lệ.
 */
export function checkStopPrecedence(stops: StopOrderItem[], noteCode: (id: number) => string = (id) => `#${id}`): string[] {
  const lastPickup = new Map<number, number>();
  const firstDrop = new Map<number, number>();
  stops.forEach((s, i) => {
    for (const n of s.noteIds) {
      if (s.kind === 'pickup') lastPickup.set(n, i);
      else if (!firstDrop.has(n)) firstDrop.set(n, i);
    }
  });
  const errors: string[] = [];
  for (const [n, d] of firstDrop) {
    const p = lastPickup.get(n);
    if (p !== undefined && p > d) errors.push(`Phiếu ${noteCode(n)}: phải lấy hàng trước khi giao`);
  }
  return errors;
}

/**
 * Lalamove không có tài xế nhận (anh Tiến 24/10/2026 "hết dân chạy lalamove", DEC-475).
 * - `expired` / `rejected`: Lalamove đã hết hạn / từ chối đơn → đặt lại loại xe khác hoặc chuyển xe công ty / xe thuê.
 * - `slow`: đã đặt / đang tìm tài xế quá `CARRIER_NO_DRIVER_MINUTES` phút → báo sớm để kịp xoay xe khác.
 */
export const CARRIER_NO_DRIVER_MINUTES = 15;
export type CarrierNoDriver = { kind: 'expired' | 'rejected' | 'slow'; minutes: number };
export function carrierNoDriver(o: { status: string; placedAt: string | Date | null }, now = Date.now()): CarrierNoDriver | null {
  const minutes = o.placedAt ? Math.max(0, Math.floor((now - new Date(o.placedAt).getTime()) / 60_000)) : 0;
  if (o.status === 'expired' || o.status === 'rejected') return { kind: o.status, minutes };
  if ((o.status === 'placed' || o.status === 'assigning') && o.placedAt && minutes >= CARRIER_NO_DRIVER_MINUTES) return { kind: 'slow', minutes };
  return null;
}
